import { Form, FormEvents, ICheckboxGroupValue } from "@dhx/ts-form";
import { FilterConditions, IFilter, IPageMeta } from "../types";
import locale from "../locales/en";
import { Popup, PopupEvents } from "@dhx/ts-popup";
import { getCellIds, getCellNameByIndex } from "../helpers/main";
import { locateNode } from "@dhx/ts-common/html";
import { DataPage } from "@xbs/muon";
import { excelDateToJSDate, getExcelDateFromJs, getExcelDateFromString } from "../helpers/date";
import { compare, isDefined, isNumeric } from "@dhx/ts-common/core";
import { Grid } from "@dhx/ts-grid";
import { Combo } from "@dhx/ts-form/sources/elements/combo";
import { CheckboxGroup } from "@dhx/ts-form/sources/elements/checkboxGroup";
import { Button } from "@dhx/ts-form/sources/elements/button";
import { Input } from "@dhx/ts-form/sources/elements/input";
import { DatePicker } from "@dhx/ts-form/sources/elements/dateinput";
import { getFormattedValue } from "../helpers/format";
import { awaitRedraw } from "@dhx/ts-common/dom";
import { IValue } from "@xbs/muon/dist/types/types";

export class FilterForm {
	protected _handlers: { [key: string]: (...args) => void };
	private _form: Form;
	private _popup: Popup;
	private _data: any[];
	private _grid: Grid;
	private _ruleIndex: number;
	private _filter: IFilter;
	private _checkboxesState: ICheckboxGroupValue;
	private _set: any;
	private _initValue: any;
	private _controls: {
		select: Button;
		checkboxes: CheckboxGroup;
		condition: Combo;
		input: Input;
		unselect: Button;
		search: Input;
		datepicker: DatePicker;
	};
	private _hidden: any[];

	constructor(grid: Grid, set: any) {
		this._grid = grid;
		this._set = set;
		this._popup = new Popup({
			css: "dhx_editor-popup dhx_filter-popup",
		});
	}

	hide() {
		if (this._popup.isVisible()) {
			this._popup.hide();
			awaitRedraw().then(() => this._form.destructor());
		}
	}

	show(cell: string, e: MouseEvent, filter: IFilter, page: DataPage) {
		const node = locateNode(e, "data-dhx-col-id") as HTMLAnchorElement;
		this._initForm();
		const { cell: filteredCell, rules } = filter;
		const { start, end } = getCellIds(filteredCell, this._grid);
		const { row, col } = getCellIds(cell, this._grid);
		this._filter = { ...filter, rules: [...filter.rules] };
		this._ruleIndex = col - start.col;
		const rule = rules[this._ruleIndex];
		const startRow = row + 1;
		const data: IValue[] = page.getCellRange(startRow, col, end.row, col).map((item, index) => {
			item = item || {};
			const cellId = isDefined(item.value)
				? getCellNameByIndex(startRow + index, col + 1, this._grid)
				: "empty";
			return { ...item, id: cellId };
		});
		const state = {};
		this._hidden = [];
		this._data = [];
		const uniqueValues: any[] = [];
		const { condition, exclude } = rule;
		data.forEach((item, index) => {
			const rowId = index + startRow;
			const h = this._grid.isRowHidden(rowId + 1);
			const { value, id , format} = item || { id: "empty" };
			const excluded = isDefined(exclude) && exclude?.find(i => {
				if (format?.id) {
					i = typeof i === "object" ? getExcelDateFromJs(i) : getExcelDateFromString(i, format);	
				}
				return i === value
			});
			const hidden = h && !excluded;
			if (hidden || uniqueValues.includes(value)) {
				// items that where hidden by prev filter rule
				if (value) {
					this._hidden.push(item);
				}
				return;
			}
			const checked = !excluded;
			uniqueValues.push(value);
			state[id] = checked;
			this._data.push({ ...item, checked });
		});

		this._setCheckboxes();
		const { input, condition: combo, checkboxes, search } = this._controls;
		checkboxes.setValue(state);
		this._checkboxesState = checkboxes.getValue();
		input.hide();
		input.setValue("");
		search.setValue("");
		if (condition?.factor) {
			const { factor, value } = condition;
			this._updateStateByCondition(factor, value);
		} else {
			combo.setValue("1");
			input.hide();
		}

		this._popup.show(node);
		this._initValue = this._form.getValue();
	}

	apply() {
		const formval = this._form.getValue();
		const { condition, value, max } = formval;
		const exclude = [];
		const index = Object.values(this._checkboxesState).findIndex(i => !i);
		if (index > -1) {
			Object.keys(this._checkboxesState).forEach(key => {
				if (!this._checkboxesState[key]) {
					const val = this._data.find(i => i.id == key).value;
					exclude.push(isDefined(val) ? val : null);
				}
			});
		}
		let cond;
		let inputValue = value;
		if (condition !== 1) {
			switch (condition) {
				case FilterConditions.tdc: 
					if (!value) {
						inputValue = null;
					}
					break;
				case FilterConditions.d:
				case FilterConditions.da:
				case FilterConditions.db:
					const date = this._controls.datepicker.getValue(true) as Date;
					if (date) {
						inputValue = getExcelDateFromJs(date);
					}
					break;
				case FilterConditions.ib:
				case FilterConditions.inb:
					if (typeof value === "number" && typeof max === "number") {
						inputValue = [Math.min(value, max), Math.max(value, max)];
					}
					break;
				case FilterConditions.e:
				case FilterConditions.ne:
					inputValue = true;
					break;
				default:
					inputValue = value;
			}
			if (isDefined(inputValue)) {
				cond = {
					factor: condition,
					value: inputValue,
				};
			}
		}
		if (!compare(this._initValue, formval)) {
			this._filter.rules[this._ruleIndex] = { condition: cond, exclude };
			this._set(this._filter.cell, this._filter.rules);
		}
		this.hide();
	}

	protected _initForm() {
		this._form = new Form(null, {
			css: "dhx_spreadsheet_filter-popup",
			rows: [
				{
					name: "condition",
					type: "combo",
					label: `${locale.condition}:`,
					labelPosition: "top",
					readonly: true,
					height: 70,
					data: [
						{ value: locale.none, id: 1 },
						...Object.keys(FilterConditions).map(key => ({
							value: locale[key],
							id: key,
						})),
					],
				},
				{
					name: "value",
					type: "input",
					placeholder: locale.value,
					height: 32,
				},
				{
					name: "date",
					type: "datepicker",
					placeholder: locale.date,
					height: 32,
				},
				{
					type: "text",
					name: "and",
					value: locale.and,
					height: 32,
				},
				{
					name: "max",
					type: "input",
					inputType: "number",
					height: 32,
					placeholder: locale.value,
				},
				{
					type: "spacer",
					height: 10,
				},
				{
					type: "text",
					css: "dhx-filter-label",
					height: 32,
					value: `${locale.values}:`,
				},
				{
					type: "button",
					name: "unselect",
					view: "flat",
					full: true,
					css: "filter-unselect",
					text: locale.unselectAll,
				},
				{
					type: "button",
					name: "select",
					view: "flat",
					full: true,
					hidden: true,
					text: locale.selectAll,
				},
				{
					type: "spacer",
					height: 5,
				},
				{
					type: "input",
					name: "search",
					height: 50,
					icon: "dxi dxi-magnify",
					autocomplite: true,
				},

				{
					css: "checkboxGroup",
					rows: [
						{
							name: "checkboxes",
							type: "checkboxGroup",
							options: {
								rows: [],
							},
						},
					],
				},
				{
					type: "spacer",
					height: 15,
				},
				{
					css: "buttons-wrapper",
					cols: [
						{
							name: "cancel",
							type: "button",
							view: "link",
							text: locale.cancel,
						},
						{
							name: "apply",
							type: "button",
							view: "flat",
							text: locale.apply,
						},
					],
				},
			],
		});
		const select = this._form.getItem("select") as Button;
		const unselect = this._form.getItem("unselect") as Button;
		const checkboxes = this._form.getItem("checkboxes") as CheckboxGroup;
		const condition = this._form.getItem("condition") as Combo;
		const input = this._form.getItem("value") as Input;
		const search = this._form.getItem("search") as Input;
		const datepicker = this._form.getItem("date") as DatePicker;
		this._controls = {
			unselect,
			select,
			checkboxes,
			condition,
			input,
			search,
			datepicker,
		};

		this._checkboxesState = {};
		this._popup.attach(this._form);
		this._initHandlers();
	}

	protected _updateStateByCondition(condition: string, val?: any) {
		const { input, condition: combo, datepicker } = this._controls;
		combo.setValue(condition);
		const max = this._form.getItem("max");
		const and = this._form.getItem("and");
		max.hide();
		and.hide();
		datepicker.hide();

		input.setValue(val);
		switch (condition) {
			case FilterConditions.d:
			case FilterConditions.da:
			case FilterConditions.db:
				datepicker.show();
				datepicker.setValue(isNumeric(val) ? excelDateToJSDate(val) : "");
				input.hide();
				break;
			case FilterConditions.e:
			case FilterConditions.ne:
				input.hide();
				break;
			case FilterConditions.tc:
			case FilterConditions.tdc:
			case FilterConditions.ts:
			case FilterConditions.te:
			case FilterConditions.tex:
				input.setProperties({
					inputType: "text",
				});
				input.setValue(val);
				input.show();
				break;
			case FilterConditions.ib:
			case FilterConditions.inb:
				and.show();
				max.setValue(val?.[1]);
				max.show();
			case FilterConditions.gt:
			case FilterConditions.geq:
			case FilterConditions.lt:
			case FilterConditions.leq:
			case FilterConditions.eq:
			case FilterConditions.neq:
				input.setProperties({
					inputType: "number",
				});
				input.setValue(val instanceof Array ? val[0] : val);
				input.show();
				break;
			default:
				input.hide();
		}
	}
	applyFilter(page: DataPage) {
		const { filter } = page.getMeta() as IPageMeta;
		this._grid.data.getInitialData().forEach(({ id }) => {
			if (this._grid.isRowHidden(id)) {
				this._showRow(id);
			}
		});
		if (!filter) {
			this._grid.paint();
			this._updateGridData();
			return;
		}
		const { cell, rules } = filter;
		const cellIds = getCellIds(cell, this._grid);
		const { start, end } = cellIds;
		let index = 0;
		let endCol = end.col;
		let endRow = end.row;

		// exclude first row form filtering
		const startRow = start.row + 1;
		for (let c = start.col; c <= endCol; c++) {
			const { exclude, condition } = rules[index];
			index++;
			let columnData = page.getCellRange(startRow, c, endRow, c).map((c, index) => {
				const rowId = index + startRow + 1;
				return {...c, rowId};
			});
			// filter by values
			if (exclude) {
				columnData = columnData.filter((c, index) => {
					const cell:IValue = {
						...c,
						value: isDefined(c?.value) ? c.value : null,
					};
					const {format} = cell;
					const hidden = exclude.find(v => {
						if (format?.id === "date") {
							v = typeof v === "object" ? getExcelDateFromJs(v) : getExcelDateFromString(v, format);
						}
						return cell.value === v;
						
					});
					const rowId = index + startRow + 1;
					if (hidden && !this._grid.isRowHidden(rowId)) {
						this._hideRow(rowId);
					}
					return !hidden;
				});
			}
			// filter by conditions
			if (condition) {
				let { factor, value } = condition;
				columnData.forEach(c => {
					const { value: val, format, rowId } = (c || {}) as any;
					let hide;
					let formatedValue = getFormattedValue(val, format);
					switch (factor) {
						case FilterConditions.d:
						case FilterConditions.da:
						case FilterConditions.db:
							value = typeof value === "object" ? getExcelDateFromJs(value) : getExcelDateFromString(value, format);
							formatedValue = val;
					}
					switch (factor) {
						case FilterConditions.e:
							hide = isDefined(val);
							break;
						case FilterConditions.ne:
							hide = !isDefined(val);
							break;
						case FilterConditions.tc:
							hide = (value && !val) || val && !`${formatedValue}`.toLowerCase().includes(value.toLowerCase());
							break;
						case FilterConditions.tdc:
							hide = val && `${formatedValue}`.toLowerCase().includes(value.toLowerCase());
							break;
						case FilterConditions.ts:
							hide = val && !`${formatedValue}`.toLowerCase().startsWith(value.toLowerCase());
							break;
						case FilterConditions.te:
							hide = val && !`${formatedValue}`.toLowerCase().endsWith(value.toLowerCase());
							break;
						case FilterConditions.tex:
							hide = isNumeric(formatedValue) || `${formatedValue}` !== value;
							break;
						case FilterConditions.d:
							hide =
								!isDefined(val) || !isNumeric(val) || Math.trunc(val) !== Math.trunc(value);
							break;
						case FilterConditions.db:
							hide = !isDefined(val) || !isNumeric(val) || Math.trunc(val) >= Math.trunc(value);
							break;
						case FilterConditions.da:
							hide = !isDefined(val) || !isNumeric(val) || Math.trunc(val) <= Math.trunc(value);
							break;
						case FilterConditions.gt:
							hide = !isDefined(val) || !isNumeric(val) || val <= value;
							break;
						case FilterConditions.lt:
							hide = !isDefined(val) || !isNumeric(val) || val >= value;
							break;
						case FilterConditions.geq:
							hide = !isDefined(val) || !isNumeric(val) || val < value;
							break;
						case FilterConditions.leq:
							hide = !isDefined(val) || !isNumeric(val) || val > value;
							break;
						case FilterConditions.eq:
							hide = !isDefined(val) || !isNumeric(val) || val !== value;
							break;
						case FilterConditions.neq:
							hide = !isDefined(val) || !isNumeric(val) || val === value;
							break;
						case FilterConditions.ib:
							hide = !isDefined(val) || !isNumeric(val) || val < value[0] || val > value[1];
							break;
						case FilterConditions.inb:
							hide = !isDefined(val) || !isNumeric(val) || (val >= value[0] && val <= value[1]);
							break;
					}
					
					if (hide && !this._grid.isRowHidden(rowId)) {
						this._hideRow(rowId);
					}
				});
			}
		}
		this._updateGridData();
		this._grid.paint();
	}
	protected _showRow(id) {
		this._grid.data.update(id, { hidden: false }, true);
	}
	protected _hideRow(id) {
		this._grid.data.update(id, { hidden: true }, true);
	}
	protected _updateGridData() {
		this._grid.data.filter(row => !row.hidden);
	}

	protected _updateButton(selectAll?: boolean) {
		const { select, unselect } = this._controls;
		awaitRedraw().then(() => {
			if (selectAll) {
				unselect.show();
				select.hide();
			} else {
				unselect.hide();
				select.show();
			}
		});
	}

	protected _setCheckboxes(search = "") {
		const options = this._data
			.filter(
				({ value, format }) =>
					!search ||
					`${getFormattedValue(value, format)}`
						.toLocaleLowerCase()
						.includes(search.toLocaleLowerCase())
			)
			.map(({ value, checked, id, format }) => {
				const val = getFormattedValue(value, format);
				return {
					type: "checkbox",
					text: val ?? `(${locale.empty})`,
					id,
					checked,
				};
			});
		this._form.getItem("checkboxes").setProperties({ options: { rows: [...options] } });
	}
	protected _selectAll(select) {
		const { checkboxes } = this._controls;
		Object.keys(this._checkboxesState).forEach(key => (this._checkboxesState[key] = select));
		checkboxes.setValue(this._checkboxesState);
		this._updateButton(select);
	}

	protected _initHandlers() {
		const { condition, search, checkboxes, datepicker } = this._controls;
		const combo = condition.getWidget();

		this._form.events.on(FormEvents.change, (name, val) => {
			switch (name) {
				case "condition":
					this._updateStateByCondition(val);
					break;
				case "checkboxes":
					this._checkboxesState = { ...this._checkboxesState, ...val };
					const selectAll = Object.values(this._checkboxesState).findIndex(i => !i);
					this._updateButton(selectAll < 0);
					break;
			}
		});
		search.events.on("input", (text: string | number) => {
			this._setCheckboxes(`${text}`);
			checkboxes.setValue(this._checkboxesState);
		});
		this._form.events.on(FormEvents.click, name => {
			switch (name) {
				case "unselect":
				case "select":
					this._selectAll(name === "select");
					break;
				case "apply":
					this.apply();
					break;
				case "cancel":
					this.hide();
					break;
			}
		});

		// prevent bug from suite
		const comboPopup = combo.popup.getContainer();
		const calendarPopup = (datepicker as any)._popup.getContainer();
		this._popup.events.on(PopupEvents.beforeHide, (fromouterclick, e) => {
			if (fromouterclick) {
				const parentPopup = (e?.target as HTMLElement).closest(".dhx_popup-content");
				if (fromouterclick && (parentPopup === comboPopup || parentPopup === calendarPopup))
					return false;
			}
		});
	}
}
